import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RequestQuote = () => {
  const [categories, setCategories] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    product_type: "",
    quantity: "",
    deadline: "",
    description: "",
  });

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get(`${API}/categories`);
        setCategories(response.data);
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };
    fetchCategories();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.phone || !formData.product_type || !formData.quantity || !formData.description) {
      toast.error("Please fill in all required fields");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/quotes`, formData);
      setSubmitted(true);
      toast.success("Quote request submitted successfully!");
    } catch (error) {
      console.error("Error submitting quote:", error);
      toast.error("Failed to submit quote request. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const productTypes = [
    "Business Cards",
    "Labels & Stickers",
    "Promotional Items",
    "Signs & Banners",
    "Posters",
    "Flyers & Brochures",
    "T-Shirt Printing",
    "Custom Merchandise",
    "Other",
  ];

  return (
    <div className="min-h-screen pt-20" data-testid="request-quote-page">
      {/* Header */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-[#141414] border-b border-white/10">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="font-outfit text-4xl sm:text-5xl text-white mb-4">
            Request a <span className="gold-gradient">Quote</span>
          </h1>
          <p className="text-gray-400 max-w-2xl mx-auto">
            Tell us about your project and we'll provide you with a customized quote.
          </p>
        </div>
      </section>

      {/* Form */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <div className="bg-[#141414] p-8 sm:p-10 rounded-sm border border-white/10">
            {submitted ? (
              <div className="text-center py-16">
                <div className="w-20 h-20 rounded-full bg-[#D4AF37]/20 flex items-center justify-center mx-auto mb-6">
                  <Check size={40} className="text-[#D4AF37]" />
                </div>
                <h2 className="font-outfit text-2xl text-white mb-4">Quote Request Received!</h2>
                <p className="text-gray-400 mb-8 max-w-md mx-auto">
                  Thank you for your interest. Our team will review your request and get back to you within 24-48 hours.
                </p>
                <Button
                  onClick={() => {
                    setSubmitted(false);
                    setFormData({
                      name: "", email: "", phone: "", company: "",
                      product_type: "", quantity: "", deadline: "", description: ""
                    });
                  }}
                  className="btn-outline-gold"
                >
                  Submit Another Request
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <h2 className="font-outfit text-xl text-white mb-6">Your Information</h2>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <Label htmlFor="name" className="text-gray-300 mb-2 block">
                      Full Name *
                    </Label>
                    <Input
                      id="name"
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="input-dark w-full"
                      placeholder="Your name"
                      data-testid="quote-name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="company" className="text-gray-300 mb-2 block">
                      Company Name
                    </Label>
                    <Input
                      id="company"
                      type="text"
                      value={formData.company}
                      onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                      className="input-dark w-full"
                      placeholder="Your company (optional)"
                      data-testid="quote-company"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <Label htmlFor="email" className="text-gray-300 mb-2 block">
                      Email Address *
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="input-dark w-full"
                      placeholder="Your email"
                      data-testid="quote-email"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone" className="text-gray-300 mb-2 block">
                      Phone Number *
                    </Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="input-dark w-full"
                      placeholder="Your phone"
                      data-testid="quote-phone"
                    />
                  </div>
                </div>

                <hr className="border-white/10 my-8" />
                
                <h2 className="font-outfit text-xl text-white mb-6">Project Details</h2>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <Label htmlFor="product_type" className="text-gray-300 mb-2 block">
                      Product Type *
                    </Label>
                    <Select
                      value={formData.product_type}
                      onValueChange={(value) => setFormData({ ...formData, product_type: value })}
                    >
                      <SelectTrigger className="input-dark w-full" data-testid="quote-product-type">
                        <SelectValue placeholder="Select product type" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#141414] border-white/10">
                        {productTypes.map((type) => (
                          <SelectItem 
                            key={type} 
                            value={type}
                            className="text-gray-300 focus:bg-[#D4AF37]/20 focus:text-white"
                          >
                            {type}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="quantity" className="text-gray-300 mb-2 block">
                      Quantity *
                    </Label>
                    <Input
                      id="quantity"
                      type="text"
                      value={formData.quantity}
                      onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                      className="input-dark w-full"
                      placeholder="e.g., 500, 1000"
                      data-testid="quote-quantity"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="deadline" className="text-gray-300 mb-2 block">
                    Deadline / Timeline
                  </Label>
                  <Input
                    id="deadline"
                    type="text"
                    value={formData.deadline}
                    onChange={(e) => setFormData({ ...formData, deadline: e.target.value })}
                    className="input-dark w-full"
                    placeholder="When do you need this? (optional)"
                    data-testid="quote-deadline"
                  />
                </div>

                <div>
                  <Label htmlFor="description" className="text-gray-300 mb-2 block">
                    Project Description *
                  </Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="input-dark w-full min-h-[150px]"
                    placeholder="Please describe your project in detail. Include any specifications, sizes, colors, or special requirements."
                    data-testid="quote-description"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full btn-gold text-lg py-6"
                  data-testid="quote-submit-btn"
                >
                  {submitting ? "Submitting..." : "Submit Quote Request"}
                </Button>

                <p className="text-gray-500 text-sm text-center">
                  We typically respond within 24-48 business hours.
                </p>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default RequestQuote;
